import { useEffect, useState } from "react";
import { Loader2, Megaphone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NoticeBand } from "@/components/NoticeBand";
import { api, apiError, fmtWhen } from "@/lib/api";

const BLANK = { message: "", tone: "gold", link: "", show_login: true, show_portal: true };

const Count = ({ label, value, testId }) => (
  <div className="bg-card p-4" data-testid={testId}>
    <div className="label-caps">{label}</div>
    <div className="font-data mt-1 text-2xl font-black tabular-nums">{value}</div>
  </div>
);

/**
 * The notice board — the portal's second band, written by a super-admin.
 *
 * Several notices may be kept; the newest one switched on for a place is the one that band shows.
 * A notice is retired by switching it off, so what was once announced stays on record.
 */
export default function NoticeBoard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [beat, setBeat] = useState(0);

  const load = () =>
    api
      .get("/board/notices")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const write = async () => {
    if (!form.message.trim()) {
      toast.error("Write the notice first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/board/notices", form);
      toast.success("On the board. The band is already carrying it.");
      setForm(BLANK);
      setBeat((b) => b + 1);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const flip = async (notice, field, on) => {
    try {
      await api.patch(`/board/notices/${notice.id}`, { [field]: on });
      setBeat((b) => b + 1);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (notice) => {
    if (!window.confirm(`Take "${notice.message}" off the board altogether?`)) return;
    try {
      await api.delete(`/board/notices/${notice.id}`);
      toast.success("Taken off the board.");
      setBeat((b) => b + 1);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="p-5 sm:p-8" data-testid="notice-board-page">
      <div className="label-caps flex items-center gap-1.5">
        <Megaphone className="h-3 w-3" /> Super-admin
      </div>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Notice board</h1>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        The second band, beside SAIL's standing mission line. Write what every officer should read,
        pick its colour, and switch it on for the sign-in page, for inside the portal, or both. The
        newest notice switched on for a place is the one that band carries.
      </p>

      <div className="mt-6 grid gap-px bg-border sm:grid-cols-3">
        <Count label="Notices written" value={data.totals.written} testId="board-total-written" />
        <Count label="On the sign-in page" value={data.totals.on_login} testId="board-total-login" />
        <Count label="Inside the portal" value={data.totals.in_portal} testId="board-total-portal" />
      </div>

      <div className="mt-6 border border-border bg-card p-5" data-testid="board-compose">
        <div className="label-caps">Write a notice</div>
        <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_200px_240px]">
          <label className="block">
            <span className="font-data block text-[10px] text-muted-foreground">
              What it should say
            </span>
            <Input
              data-testid="board-message"
              value={form.message}
              maxLength={220}
              placeholder="Review meeting with the Director on 20 Oct — units to update the register by 18 Oct"
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 h-11 rounded-none"
            />
          </label>
          <label className="block">
            <span className="font-data block text-[10px] text-muted-foreground">Colour</span>
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="board-tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {data.tones.map((t) => (
                  <SelectItem key={t.key} value={t.key} data-testid={`board-tone-${t.key}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="font-data block text-[10px] text-muted-foreground">
              A link, if it has one
            </span>
            <Input
              data-testid="board-link"
              value={form.link}
              placeholder="https://…"
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              className="mt-1 h-11 rounded-none"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="board-show-login"
              checked={form.show_login}
              onCheckedChange={(v) => setForm({ ...form, show_login: v })}
            />
            Show it on the sign-in page
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="board-show-portal"
              checked={form.show_portal}
              onCheckedChange={(v) => setForm({ ...form, show_portal: v })}
            />
            Show it inside the portal
          </label>
          <button
            type="button"
            data-testid="board-write"
            disabled={busy}
            onClick={write}
            className="flex h-11 items-center gap-2 bg-primary px-5 font-display text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Put it on the board
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="label-caps">What the bands are carrying right now</div>
        <div className="mt-2 space-y-px" data-testid="board-preview">
          <NoticeBand where="portal" refreshKey={beat} />
          {!data.live.portal && !data.live.login && (
            <p className="border border-border bg-card p-4 text-sm text-muted-foreground">
              Nothing is switched on, so neither band is drawn at all — the portal never carries an
              empty strip.
            </p>
          )}
        </div>
      </div>

      <div className="mt-8 divide-y divide-border border border-border bg-card">
        {data.rows.map((n) => (
          <div
            key={n.id}
            className="flex flex-wrap items-center gap-4 p-4"
            data-testid={`board-row-${n.id}`}
          >
            <span className="min-w-0 flex-1">
              <span className="font-data block text-[10px] uppercase tracking-widest text-muted-foreground">
                {n.tone} · by {n.by} · {fmtWhen(n.at)}
                {n.updated_at ? ` · changed by ${n.updated_by} ${fmtWhen(n.updated_at)}` : ""}
              </span>
              <span className="mt-0.5 block text-sm font-semibold">{n.message}</span>
              {n.link && (
                <span className="font-data block text-[10px] text-muted-foreground">{n.link}</span>
              )}
            </span>
            <label className="flex items-center gap-2 text-[11px]">
              <Switch
                data-testid={`board-toggle-login-${n.id}`}
                checked={n.show_login}
                onCheckedChange={(v) => flip(n, "show_login", v)}
              />
              Sign-in page
            </label>
            <label className="flex items-center gap-2 text-[11px]">
              <Switch
                data-testid={`board-toggle-portal-${n.id}`}
                checked={n.show_portal}
                onCheckedChange={(v) => flip(n, "show_portal", v)}
              />
              Inside the portal
            </label>
            <button
              type="button"
              data-testid={`board-remove-${n.id}`}
              onClick={() => remove(n)}
              className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--at-risk))]"
              title="Take it off the board"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!data.rows.length && (
          <p className="p-8 text-sm text-muted-foreground" data-testid="board-empty">
            No notice has been written yet. Whatever you write above goes up on the band at once.
          </p>
        )}
      </div>
    </div>
  );
}
